/**
 * lib/indicators.ts — direct port of app/indicators.py.
 *
 * Same function names, same algorithms, same default arguments. Pure
 * functions, no dependencies. Tested against the Python output: feed
 * the same OHLCV series in, get the same indicator series out.
 *
 * Data shape: instead of pandas DataFrames/Series we use plain arrays
 * of numbers (open[], high[], low[], close[], volume[]). The Bar type
 * groups them when convenient. NaN sentinels stand in for pandas NaN.
 */

export interface Bar {
  time:   number;  // unix seconds
  open:   number;
  high:   number;
  low:    number;
  close:  number;
  volume: number;
}

// ── Helpers ────────────────────────────────────────────────────────────────

/** Rolling simple mean. Returns NaN for the first (n-1) positions. */
export function rollingMean(x: number[], n: number): number[] {
  const out = new Array<number>(x.length);
  let sum = 0;
  for (let i = 0; i < x.length; i++) {
    sum += x[i] ?? 0;
    if (i >= n) sum -= x[i - n] ?? 0;
    out[i] = i >= n - 1 ? sum / n : NaN;
  }
  return out;
}

/** Rolling max over window n. NaN for the first (n-1) positions. */
export function rollingMax(x: number[], n: number): number[] {
  const out = new Array<number>(x.length);
  for (let i = 0; i < x.length; i++) {
    if (i < n - 1) { out[i] = NaN; continue; }
    let m = -Infinity;
    for (let j = i - n + 1; j <= i; j++) if (x[j] > m) m = x[j];
    out[i] = m;
  }
  return out;
}

/** Rolling min over window n. NaN for the first (n-1) positions. */
export function rollingMin(x: number[], n: number): number[] {
  const out = new Array<number>(x.length);
  for (let i = 0; i < x.length; i++) {
    if (i < n - 1) { out[i] = NaN; continue; }
    let m = Infinity;
    for (let j = i - n + 1; j <= i; j++) if (x[j] < m) m = x[j];
    out[i] = m;
  }
  return out;
}

/** pandas-style EWM mean with `span` parameter. alpha = 2 / (span + 1). */
export function ewmMean(x: number[], span: number): number[] {
  const alpha = 2 / (span + 1);
  const out = new Array<number>(x.length);
  let prev = NaN;
  for (let i = 0; i < x.length; i++) {
    if (i === 0) { prev = x[0]; out[0] = prev; continue; }
    prev = alpha * x[i] + (1 - alpha) * prev;
    out[i] = prev;
  }
  return out;
}

/** First difference: out[i] = x[i] - x[i-1]; out[0] = NaN. */
export function diff(x: number[]): number[] {
  const out = new Array<number>(x.length);
  out[0] = NaN;
  for (let i = 1; i < x.length; i++) out[i] = x[i] - x[i - 1];
  return out;
}

/** Cumulative sum. */
export function cumsum(x: number[]): number[] {
  const out = new Array<number>(x.length);
  let s = 0;
  for (let i = 0; i < x.length; i++) { s += x[i] ?? 0; out[i] = s; }
  return out;
}

/** Math.sign with NaN passthrough. */
function sgn(x: number): number {
  if (Number.isNaN(x)) return 0;
  return x > 0 ? 1 : x < 0 ? -1 : 0;
}

// ── Price indicators (1:1 ports of the Python functions) ──────────────────

/**
 * RSI(c, n=14) — Wilder's RSI.
 *
 * This previously used a SIMPLE rolling mean of gains and losses while
 * documenting itself as Wilder's. Wilder smooths recursively
 * (avg = (prev·(n−1) + current) / n), which decays old observations
 * instead of dropping them off a cliff at the window edge. The two agree
 * in shape but not in value, so the terminal printed an RSI that did not
 * match TradingView or the trader's broker — a number people take
 * positions on. Implemented properly here.
 */
export function RSI(close: number[], n = 14): number[] {
  const out = new Array<number>(close.length).fill(NaN);
  if (close.length <= n) return out;

  const d = diff(close);   // d[0] is NaN; d[i] = close[i] - close[i-1]

  // Seed with the simple average of the first n changes, as Wilder does.
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= n; i++) {
    const ch = d[i];
    if (Number.isNaN(ch)) continue;
    if (ch > 0) avgGain += ch; else avgLoss += -ch;
  }
  avgGain /= n; avgLoss /= n;
  out[n] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);

  // Then smooth recursively.
  for (let i = n + 1; i < close.length; i++) {
    const ch = Number.isNaN(d[i]) ? 0 : d[i];
    const gain = ch > 0 ? ch : 0;
    const loss = ch < 0 ? -ch : 0;
    avgGain = (avgGain * (n - 1) + gain) / n;
    avgLoss = (avgLoss * (n - 1) + loss) / n;
    out[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return out;
}

/** MACD(c, f=12, s=26, sg=9) -> [macdLine, signal, histogram] */
export function MACD(
  close: number[], f = 12, s = 26, sg = 9,
): { macd: number[]; signal: number[]; hist: number[] } {
  const fast = ewmMean(close, f);
  const slow = ewmMean(close, s);
  const macd = fast.map((v, i) => v - slow[i]);
  const signal = ewmMean(macd, sg);
  const hist = macd.map((v, i) => v - signal[i]);
  return { macd, signal, hist };
}

/**
 * ATR(n=14) — Average True Range, Wilder-smoothed.
 *
 * Was a simple rolling mean of true range, which is not what "ATR" means
 * to any charting package a trader will compare against. ATR is commonly
 * used to size stops, so a value that disagrees with the broker's chart
 * translates directly into a mis-sized stop.
 */
export function ATR(high: number[], low: number[], close: number[], n = 14): number[] {
  const len = close.length;
  const out = new Array<number>(len).fill(NaN);
  if (len === 0) return out;

  const tr = new Array<number>(len);
  for (let i = 0; i < len; i++) {
    if (i === 0) { tr[i] = high[i] - low[i]; continue; }
    const a = high[i] - low[i];
    const b = Math.abs(high[i] - close[i - 1]);
    const c = Math.abs(low[i]  - close[i - 1]);
    tr[i] = Math.max(a, b, c);
  }
  if (len < n) return out;

  // Seed with the simple mean of the first n true ranges, then smooth.
  let atr = 0;
  for (let i = 0; i < n; i++) atr += tr[i];
  atr /= n;
  out[n - 1] = atr;
  for (let i = n; i < len; i++) {
    atr = (atr * (n - 1) + tr[i]) / n;
    out[i] = atr;
  }
  return out;
}

/** OBV(c, v) — On-Balance Volume. */
export function OBV(close: number[], volume: number[]): number[] {
  const d = diff(close);
  return cumsum(d.map((dv, i) => sgn(dv) * (volume[i] ?? 0)));
}

/**
 * STOCH(df, k=14, d=3) — %K and %D.
 * %K = 100 * (close - low_k) / (high_k - low_k + 1e-10)
 * %D = rolling mean of %K over d.
 */
export function STOCH(
  high: number[], low: number[], close: number[], k = 14, d = 3,
): { pk: number[]; pd: number[] } {
  const hi = rollingMax(high, k);
  const lo = rollingMin(low,  k);
  const pk = close.map((c, i) => 100 * (c - lo[i]) / ((hi[i] - lo[i]) + 1e-10));
  const pd = rollingMean(pk, d);
  return { pk, pd };
}

/**
 * ADON(df, fast=10, slow=50) — Adaptive Donchian Channel.
 * Blends fast/slow Donchian bands by an ATR-percentile weight, so the
 * channel widens when volatility is high vs its own 200-bar history.
 */
export function ADON(
  high: number[], low: number[], close: number[], fast = 10, slow = 50,
): { upper: number[]; lower: number[]; mid: number[] } {
  const atr = ATR(high, low, close, 14);
  const vola = atr.map((a, i) => (close[i] ? a / close[i] : NaN));
  // pandas .rank(pct=True) over a 200-window: percentile rank of last value.
  const win = 200, minP = 20;
  const vp = new Array<number>(vola.length).fill(0.5);
  for (let i = 0; i < vola.length; i++) {
    const start = Math.max(0, i - win + 1);
    const slice = vola.slice(start, i + 1).filter(v => !Number.isNaN(v));
    if (slice.length < minP) { vp[i] = 0.5; continue; }
    const v = vola[i];
    if (Number.isNaN(v)) { vp[i] = 0.5; continue; }
    const ranked = slice.filter(x => x <= v).length;
    vp[i] = ranked / slice.length;
  }

  const hF = rollingMax(high, fast);
  const lF = rollingMin(low,  fast);
  const hS = rollingMax(high, slow);
  const lS = rollingMin(low,  slow);
  const upper = high.map((_, i) => vp[i] * hF[i] + (1 - vp[i]) * hS[i]);
  const lower = high.map((_, i) => vp[i] * lF[i] + (1 - vp[i]) * lS[i]);
  const mid   = upper.map((u, i) => (u + lower[i]) / 2);
  return { upper, lower, mid };
}

/**
 * VPRO(df, bins=40) — Volume Profile with VAH/VAL.
 *
 * Returns:
 *   centers : price level (mid of each bin)
 *   profile : volume traded at each bin
 *   poc     : Point of Control (highest-volume price level)
 *   vah     : Value Area High — top of the 70% volume band
 *   val     : Value Area Low — bottom of the 70% volume band
 *
 * VAH/VAL are computed via the standard market-profile "double-stepping"
 * algorithm: starting at the POC, walk outward — at each step compare the
 * bin one above the current high vs the bin one below the current low,
 * absorb the larger of the two, repeat until cumulative volume ≥ 70% of
 * total. The resulting band spans VAL .. VAH around the POC.
 */
export function VPRO(
  close: number[], volume: number[], high: number[], low: number[],
  bins = 40, valuePct = 0.70,
): { centers: number[]; profile: number[]; poc: number; vah: number; val: number } {
  if (close.length === 0 || high.length === 0 || low.length === 0) {
    return { centers: [], profile: [], poc: NaN, vah: NaN, val: NaN };
  }
  // O(n) min/max. `Math.min(...low)` spreads the ENTIRE series as call
  // arguments, which throws RangeError (call-stack overflow) once the bar count
  // is large -- reachable straight from the UI (e.g. MAX x 1m) -- and, because
  // Volume Profile is on by default, that throw propagated out of the render
  // memo and dropped the whole Terminal panel to the ErrorBoundary. A plain
  // loop is unbounded-safe.
  let lo = low[0];
  for (let i = 1; i < low.length; i++) if (low[i] < lo) lo = low[i];
  let hi = high[0];
  for (let i = 1; i < high.length; i++) if (high[i] > hi) hi = high[i];
  const step = (hi - lo) / bins;
  if (step <= 0) return { centers: [lo], profile: [0], poc: lo, vah: lo, val: lo };

  const edges = new Array<number>(bins + 1);
  for (let i = 0; i <= bins; i++) edges[i] = lo + step * i;

  const centers = new Array<number>(bins);
  for (let i = 0; i < bins; i++) centers[i] = (edges[i] + edges[i + 1]) / 2;

  const profile = new Array<number>(bins).fill(0);
  for (let i = 0; i < close.length; i++) {
    let b = Math.floor((close[i] - lo) / step);
    if (b < 0) b = 0;
    if (b > bins - 1) b = bins - 1;
    profile[b] += volume[i] ?? 0;
  }

  const totalVol = profile.reduce((s, v) => s + v, 0);
  const pocIdx = profile.reduce((best, v, i, arr) => v > arr[best] ? i : best, 0);

  // Walk outward from the POC, absorbing the heavier neighbor each step
  // until we've covered `valuePct` of the total volume.
  let cum = profile[pocIdx];
  let lowIdx  = pocIdx;
  let highIdx = pocIdx;
  const target = totalVol * valuePct;
  while (cum < target && (lowIdx > 0 || highIdx < bins - 1)) {
    const upVol   = highIdx < bins - 1 ? profile[highIdx + 1] : -1;
    const downVol = lowIdx  > 0        ? profile[lowIdx  - 1] : -1;
    if (upVol >= downVol && highIdx < bins - 1) {
      highIdx += 1; cum += upVol;
    } else if (lowIdx > 0) {
      lowIdx  -= 1; cum += downVol;
    } else {
      break;
    }
  }

  return {
    centers,
    profile,
    poc: centers[pocIdx],
    vah: centers[highIdx],
    val: centers[lowIdx],
  };
}

/** PIVOT(df) — classic pivot points from the prior session (last-but-one bar). */
export function PIVOT(high: number[], low: number[], close: number[]):
  { P: number; R1: number; R2: number; R3: number; S1: number; S2: number; S3: number }
{
  const n = close.length;
  if (n < 2) return { P: NaN, R1: NaN, R2: NaN, R3: NaN, S1: NaN, S2: NaN, S3: NaN };
  const h = high[n - 2], l = low[n - 2], c = close[n - 2];
  const P = (h + l + c) / 3;
  return {
    P,
    R1: 2 * P - l,
    R2: P + (h - l),
    R3: h + 2 * (P - l),
    S1: 2 * P - h,
    S2: P - (h - l),
    S3: l - 2 * (h - P),
  };
}

// ── Black-Scholes Greeks ──────────────────────────────────────────────────

const SQRT_2     = Math.sqrt(2);
const SQRT_2_PI  = Math.sqrt(2 * Math.PI);

/** Standard normal CDF via erf (Abramowitz & Stegun 7.1.26 — same fallback the Python uses). */
function normCDF(x: number): number {
  return 0.5 * (1 + erf(x / SQRT_2));
}

/** Standard normal PDF. */
function normPDF(x: number): number {
  return Math.exp(-0.5 * x * x) / SQRT_2_PI;
}

/** erf(x) — Abramowitz & Stegun 7.1.26, max error ~1.5e-7. */
function erf(x: number): number {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  const a1 =  0.254829592, a2 = -0.284496736, a3 =  1.421413741;
  const a4 = -1.453152027, a5 =  1.061405429, p  =  0.3275911;
  const t = 1.0 / (1.0 + p * x);
  const y = 1.0 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-x * x);
  return sign * y;
}

export interface Greeks { d: number; g: number; t: number; v: number; rho: number }

/**
 * BS(S, K, T, iv, r=0.05, flag='call') — Black-Scholes pricer + Greeks.
 * S  spot, K strike, T years to expiry, iv implied vol (decimal), r risk-free.
 * Returns: d=delta, g=gamma, t=theta/day, v=vega/1%, rho=rho/1%.
 */
export function BS(
  S: number, K: number, T: number, iv: number,
  r = 0.05, flag: 'call' | 'put' = 'call',
): Greeks {
  if (T <= 0 || iv <= 0 || S <= 0 || K <= 0) {
    return { d: 0, g: 0, t: 0, v: 0, rho: 0 };
  }
  const sqrtT = Math.sqrt(T);
  const d1 = (Math.log(S / K) + (r + 0.5 * iv * iv) * T) / (iv * sqrtT);
  const d2 = d1 - iv * sqrtT;

  let delta: number, theta: number, rho: number;
  if (flag === 'call') {
    delta = normCDF(d1);
    theta = (-(S * normPDF(d1) * iv) / (2 * sqrtT)
             - r * K * Math.exp(-r * T) * normCDF(d2)) / 365;
    rho   = K * T * Math.exp(-r * T) * normCDF(d2) / 100;
  } else {
    delta = normCDF(d1) - 1;
    theta = (-(S * normPDF(d1) * iv) / (2 * sqrtT)
             + r * K * Math.exp(-r * T) * normCDF(-d2)) / 365;
    rho   = -K * T * Math.exp(-r * T) * normCDF(-d2) / 100;
  }
  return {
    d:   Math.round(delta            * 10000) / 10000,
    g:   Math.round(normPDF(d1) / (S * iv * sqrtT) * 1000000) / 1000000,
    t:   Math.round(theta            * 10000) / 10000,
    v:   Math.round(S * normPDF(d1) * sqrtT / 100 * 10000) / 10000,
    rho: Math.round(rho              * 10000) / 10000,
  };
}

// ── Convenience: SMA / EMA / VWAP / Bollinger (used by chart overlays) ────

export const SMA = (c: number[], n: number): number[] => rollingMean(c, n);
export const EMA = (c: number[], n: number): number[] => ewmMean(c, n);

/** Bollinger Bands (n=20, k=2). */
export function BB(close: number[], n = 20, k = 2):
  { mid: number[]; upper: number[]; lower: number[] }
{
  const mid = rollingMean(close, n);
  const upper = new Array<number>(close.length);
  const lower = new Array<number>(close.length);
  for (let i = 0; i < close.length; i++) {
    if (i < n - 1) { upper[i] = NaN; lower[i] = NaN; continue; }
    let s = 0;
    for (let j = i - n + 1; j <= i; j++) s += (close[j] - mid[i]) ** 2;
    const sd = Math.sqrt(s / n);
    upper[i] = mid[i] + k * sd;
    lower[i] = mid[i] - k * sd;
  }
  return { mid, upper, lower };
}

/** Session VWAP (resets daily — caller passes a single session's bars). */
export function VWAP(high: number[], low: number[], close: number[], volume: number[]): number[] {
  const out = new Array<number>(close.length);
  let cumPV = 0, cumV = 0;
  for (let i = 0; i < close.length; i++) {
    const typ = (high[i] + low[i] + close[i]) / 3;
    cumPV += typ * (volume[i] ?? 0);
    cumV  += volume[i] ?? 0;
    out[i] = cumV > 0 ? cumPV / cumV : NaN;
  }
  return out;
}
